import React, { useEffect, useMemo, useState } from "react";

/**
 * DeclarationsInteractive
 *
 * Props:
 * - userName: string
 * - issuedKeyId: string
 * - onComplete?: (doc) => void
 */

const DeclarationsInteractive = ({ userName = "Member", issuedKeyId, onComplete }) => {
  const STORAGE_KEY_PROGRESS = "members_declarations_progress_v1";
  const STORAGE_KEY_DOCS = "theirDocs";

  const declarations = useMemo(
    () => [
      {
        code: "DCL-01",
        title: "Content Moderation Responsibility",
        text: `I, ${userName}, confirm that I am wholly responsible for the moderation of material that I and my invited guests store on this platform.`,
      },
      {
        code: "DCL-02",
        title: "Landing Area Review",
        text: `I confirm that I will review and remove content uploaded to the landing area that law authorities would be concerned with.`,
      },
      {
        code: "DCL-03",
        title: "No Illegal Use",
        text: `I confirm that neither I nor any of my users will use the platform to plan or engage in any illegal activities.`,
      },
      {
        code: "DCL-04",
        title: "Payment & Data Retention",
        text: `I understand that if payment is not made, content may be permanently destroyed after 28 days past the due date.`,
      },
      {
        code: "DCL-05",
        title: "Account Security",
        text: `I will keep my credentials and API keys secure and accept responsibility for activity performed using my account.`,
      },
      {
        code: "DCL-06",
        title: "Acceptable Use",
        text: `I will not upload or share unlawful, harassing, hateful, or otherwise prohibited content and will respect others on the platform.`,
      },
      {
        code: "DCL-07",
        title: "Privacy & Data",
        text: `I understand how my data is collected, used, and protected, and I consent to essential processing required to provide the service.`,
      },
      {
        code: "DCL-08",
        title: "Age Requirement",
        text: `I confirm that I meet the minimum legal age required to use this service in my jurisdiction.`,
      },
      {
        code: "DCL-09",
        title: "Lawful Cooperation",
        text: `I acknowledge that the platform may cooperate with lawful requests from authorities consistent with its policies and applicable laws.`,
      },
      {
        code: "DCL-10",
        title: "Updates to Terms",
        text: `I acknowledge that terms and policies may be updated and that continued use implies acceptance of the current terms.`,
      },
    ],
    [userName]
  );

  const [progress, setProgress] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PROGRESS);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return declarations.map((d) => ({
      code: d.code,
      confirmed: false,
      confirmedAt: null,
      keyInput: "",
    }));
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(progress));
  }, [progress]);

  const total = declarations.length;
  const done = progress.filter((p) => p.confirmed).length;
  const allDone = done === total;

  const findIdx = (code) => progress.findIndex((p) => p.code === code);

  const handleInputChange = (code, value) => {
    const idx = findIdx(code);
    if (idx < 0) return;
    setProgress((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], keyInput: value };
      return next;
    });
  };

  const confirmItem = (code) => {
    const idx = findIdx(code);
    if (idx < 0) return;

    const item = progress[idx];
    if (!item.keyInput || item.keyInput !== issuedKeyId) {
      alert("Key ID mismatch. Please enter your issued Key ID exactly to confirm.");
      return;
    }

    setProgress((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        confirmed: true,
        confirmedAt: new Date().toISOString(),
      };
      return next;
    });
  };

  const resetItem = (code) => {
    const idx = findIdx(code);
    if (idx < 0) return;
    setProgress((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        confirmed: false,
        confirmedAt: null,
        keyInput: "",
      };
      return next;
    });
  };

  const finalDoc = useMemo(() => {
    return {
      kind: "MemberDeclarations",
      version: 1,
      userName,
      issuedKeyId,
      completedAt: allDone ? new Date().toISOString() : null,
      items: declarations.map((d) => {
        const p = progress.find((x) => x.code === d.code);
        return {
          code: d.code,
          title: d.title,
          text: d.text,
          confirmed: !!p && !!p.confirmed,
          confirmedAt: (p && p.confirmedAt) || null,
        };
      }),
    };
  }, [allDone, declarations, progress, issuedKeyId, userName]);

  const saveToTheirDocs = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_DOCS);
      const arr = raw ? JSON.parse(raw) : [];
      arr.push(finalDoc);
      localStorage.setItem(STORAGE_KEY_DOCS, JSON.stringify(arr));
      alert("Saved to theirDocs (local placeholder).");
    } catch (e) {
      alert("Could not save to theirDocs.");
    }
  };

  const notifyComplete = () => {
    if (typeof onComplete === "function") onComplete(finalDoc);
  };

  const TwoColRow = ({ left, right }) => (
    <div className="row g-2 mb-3 align-items-start">
      <div className="col-12 col-md-6">{left}</div>
      <div className="col-12 col-md-6">{right}</div>
    </div>
  );

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4 mb-0">Declarations</h1>
        <span className="badge text-bg-secondary">{done} / {total} confirmed</span>
      </div>

      <p className="mb-4">
        You must confirm all {total} attestations to proceed. These clarify responsibilities for you and for us.
        Each confirmation requires your Key ID.
      </p>

      {!allDone && (
        <div className="alert alert-info" role="alert">
          <strong>Your Key ID:</strong> <code className="user-select-all">{issuedKeyId}</code>
        </div>
      )}

      {!allDone &&
        declarations.map((d) => {
          const p =
            progress.find((x) => x.code === d.code) ||
            { confirmed: false, confirmedAt: null, keyInput: "" };

          return (
            <div className="border rounded p-3 mb-3" key={d.code}>
              <TwoColRow
                left={<strong className="d-inline-block">{d.title}</strong>}
                right={<p className="mb-0">{d.text}</p>}
              />

              {p.confirmed ? (
                <TwoColRow
                  left={<span className="text-success">Status</span>}
                  right={
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className="badge text-bg-success">CONFIRMED</span>
                      {p.confirmedAt && (
                        <small className="text-muted">on {new Date(p.confirmedAt).toLocaleString()}</small>
                      )}
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => resetItem(d.code)}>
                        Undo
                      </button>
                    </div>
                  }
                />
              ) : (
                <>
                  <TwoColRow
                    left={<label htmlFor={`key-${d.code}`} className="col-form-label">Enter Key ID to confirm:</label>}
                    right={
                      <input
                        id={`key-${d.code}`}
                        className="form-control"
                        placeholder="Paste your Key ID exactly…"
                        value={p.keyInput}
                        onChange={(e) => handleInputChange(d.code, e.target.value.trim())}
                      />
                    }
                  />
                  <TwoColRow
                    left={<span className="text-muted">Action</span>}
                    right={
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => confirmItem(d.code)}
                        disabled={!p.keyInput}
                      >
                        CONFIRM
                      </button>
                    }
                  />
                </>
              )}
            </div>
          );
        })}

      {allDone && (
        <div className="mt-4">
          <div className="alert alert-success d-flex justify-content-between align-items-center">
            <div>
              <strong>All attestations confirmed.</strong> You can print or save this record.
            </div>
            <span className="badge text-bg-success">{done}/{total}</span>
          </div>

          <div className="border rounded p-3">
            <div className="row g-2 mb-3">
              <div className="col-12 col-md-6">
                <strong>User</strong>
              </div>
              <div className="col-12 col-md-6">
                {userName}
              </div>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-12 col-md-6">
                <strong>Key ID</strong>
              </div>
              <div className="col-12 col-md-6">
                <code className="user-select-all">{issuedKeyId}</code>
              </div>
            </div>

            {finalDoc.items.map((it) => (
              <div className="row g-2 mb-3" key={it.code}>
                <div className="col-12 col-md-6">
                  <div><strong>{it.code}</strong> — {it.title}</div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="mb-1">{it.text}</div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge text-bg-success">CONFIRMED</span>
                    {it.confirmedAt && (
                      <small className="text-muted">{new Date(it.confirmedAt).toLocaleString()}</small>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => window.print()}
            >
              Print
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { saveToTheirDocs(); notifyComplete(); }}
            >
              Save to theirDocs
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeclarationsInteractive;